import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { useGummiwormContext } from "@/context/GummiwormContext";
import { AssetAmount } from "@sundaeswap/asset";
import { TDepositHistoryStatus } from "@/context/GummiwormContext/hooks/useDepositTxs";
import { formatAda } from "@/lib/utils";

const Countdown: FC<{ target: number }> = ({ target }) => {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, target - Math.floor(Date.now() / 1000)),
  );

  useEffect(() => {
    const tick = () =>
      setRemaining(Math.max(0, target - Math.floor(Date.now() / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  if (remaining <= 0) return null;
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  const text = m > 0 ? `~${m}m ${String(s).padStart(2, "0")}s` : `~${s}s`;
  return <span className="text-xs text-sr-lemon-muted tabular-nums">{text}</span>;
};

const statusConfig: Record<
  TDepositHistoryStatus,
  { label: string; color: string; spinning?: boolean }
> = {
  new: { label: "Submitting", color: "text-sr-neutral-500", spinning: true },
  pending: { label: "Waiting for approval", color: "text-sr-neutral-500", spinning: true },
  confirmed: { label: "Submitting", color: "text-sr-sky", spinning: true },
  settling: { label: "Submitting", color: "text-sr-sky", spinning: true },
  "on-chain": { label: "Waiting for settlement", color: "text-sr-sky", spinning: true },
  absorbed: { label: "Available", color: "text-sr-mint" },
  rejected: { label: "Rejected", color: "text-sr-salmon" },
  refunding: { label: "Refunding", color: "text-sr-tangerine", spinning: true },
  refunded: { label: "Refunded", color: "text-sr-tangerine" },
  failed: { label: "Failed", color: "text-sr-salmon" },
};

interface GummiwormBalanceProps {
  onViewDeposit?: (requestId: string) => void;
}

export const GummiwormBalance: FC<GummiwormBalanceProps> = ({ onViewDeposit }) => {
  const { deposits, depositTxs, accounts } = useGummiwormContext();
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());

  const myAccount = useMemo(() => accounts[0], [accounts]);

  const adaBalance = useMemo(() => {
    const ada = myAccount?.uncommittedValue.assets.find(
      (a) => a.asset.tag === "Ada",
    );
    const adaAsset = new AssetAmount(0n, { decimals: 6, assetId: "ada.lovelace" });
    if (!ada) return adaAsset;
    return adaAsset.withAmount(ada.value);
  }, [myAccount?.uncommittedValue]);

  const toggleError = useCallback((id: string) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const allDeposits = useMemo(() => {
    const items: {
      id: string;
      amount: string | null;
      status: TDepositHistoryStatus;
      hash?: string;
      errorMessage?: string;
      estimatedAbsorptionTime?: number;
    }[] = [];

    // Collect IDs from SpacetimeDB deposits first
    const seenIds = new Set<string>();

    for (const dep of deposits) {
      const txData = depositTxs.get(dep.requestId.toString());
      if (!txData) continue;
      seenIds.add(dep.requestId.toString());
      const ada = dep.value.assets.find((a) => a.asset.tag === "Ada");
      const amount = ada
        ? new AssetAmount(ada.value, { decimals: 6, assetId: "ada.lovelace" }).value.toString()
        : null;
      items.push({
        id: dep.requestId.toString(),
        amount,
        status: txData.status,
        hash: txData.hash || undefined,
        errorMessage: txData.errorMessage,
        estimatedAbsorptionTime: txData.estimatedAbsorptionTime,
      });
    }

    // Add local-only deposits not yet in SpacetimeDB
    for (const [id, tx] of depositTxs.entries()) {
      if (!seenIds.has(id)) {
        items.push({ id, amount: null, status: tx.status });
      }
    }

    return items.sort((a, b) => Number(b.id) - Number(a.id));
  }, [deposits, depositTxs]);

  const hasContent = myAccount || allDeposits.length > 0;

  if (!hasContent) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* L2 Balance */}
      {myAccount && (
        <div className="sr-card p-5">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-sr-neutral-500 uppercase tracking-wider">
                Sugar Rush Balance
              </span>
              <span className="text-xs text-sr-neutral-700">
                ID: {(() => { const id = myAccount.accountId.toString(); return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-8)}` : id; })()}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span
                className="text-3xl sm:text-4xl font-medium text-white tabular-nums"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {formatAda(adaBalance)}
              </span>
              <span className="text-sm text-sr-neutral-500">ADA</span>
            </div>
          </div>
        </div>
      )}

      {/* Deposit history */}
      {allDeposits.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-sr-neutral-500 uppercase tracking-wider px-1 mb-2">
            Recent Deposits
          </span>
          {allDeposits.map((dep) => {
            const cfg = statusConfig[dep.status];
            const isError = dep.status === "rejected" || dep.status === "failed";
            const isInProgress = cfg.spinning;
            const isExpandable =
              isError || dep.status === "refunding" || dep.status === "refunded";
            const isViewable = dep.status === "absorbed";
            const isClickable = isExpandable || isInProgress || isViewable;
            const isExpanded = expandedErrors.has(dep.id);

            const handleClick = () => {
              if ((isInProgress || isViewable) && onViewDeposit) {
                onViewDeposit(dep.id);
              } else if (isExpandable) {
                toggleError(dep.id);
              }
            };

            return (
              <div key={dep.id}>
                <div
                  className={`flex items-center justify-between p-3 rounded-lg bg-sr-surface transition-colors ${
                    isClickable ? "cursor-pointer hover:bg-sr-surface-raised" : ""
                  }`}
                  onClick={isClickable ? handleClick : undefined}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {/* Status indicator */}
                    {cfg.spinning ? (
                      <div className="w-4 h-4 shrink-0 rounded-full border-2 border-current border-t-transparent animate-spin text-sr-neutral-500" />
                    ) : dep.status === "absorbed" ? (
                      <div className="w-4 h-4 shrink-0 rounded-full bg-sr-mint flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#170B10" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    ) : isError ? (
                      <div className="w-4 h-4 shrink-0 rounded-full bg-sr-salmon flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M3 3L9 9M9 3L3 9" stroke="#170B10" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-4 h-4 shrink-0 rounded-full bg-sr-tangerine flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M6 3V6.5M6 8.5V9" stroke="#170B10" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </div>
                    )}

                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-white truncate">
                        Deposit #{dep.id}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {isInProgress && dep.estimatedAbsorptionTime && (
                          <Countdown target={dep.estimatedAbsorptionTime} />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {dep.amount && (
                      <span className="text-sm tabular-nums text-white">
                        {Number(dep.amount).toLocaleString()} ADA
                      </span>
                    )}
                    {dep.hash && (
                      <a
                        href={`https://${window.__APP_CONFIG.env === "mainnet" ? "" : "preview."}cexplorer.io/tx/${dep.hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-sr-neutral-500 hover:text-sr-oat-milk transition-colors"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M5.5 2.5H3.5C2.94772 2.5 2.5 2.94772 2.5 3.5V10.5C2.5 11.0523 2.94772 11.5 3.5 11.5H10.5C11.0523 11.5 11.5 11.0523 11.5 10.5V8.5M8.5 2.5H11.5V5.5M11.5 2.5L6.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>

                {/* Expandable error */}
                {isExpandable && isExpanded && dep.errorMessage && (
                  <div className="mx-3 px-3 py-2 text-xs text-sr-salmon bg-sr-salmon/10 rounded-b-lg -mt-1">
                    {dep.errorMessage}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
